import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  UploadCloud,
  FileSpreadsheet,
  ArrowLeft,
  RefreshCcw,
  CheckSquare,
  Square,
  Search,
  Link2,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  X,
  Camera,
  ScanSearch,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import ProductCombo from '../components/ProductCombo';
import {
  clearImportDraft,
  formatFileSize,
  guessColumns,
  isAllowedOcrFile,
  isPdfFile,
  MAX_OCR_FILE_SIZE_MB,
  money,
  normalize,
  readImportDraft,
  toNumber,
  writeImportDraft,
} from '../utils/importParsing';

/**
 * Importación de costos desde Excel (proveedor -> Impulza)
 * Objetivo:
 * - Adjuntar Excel
 * - Sugerir columnas (match y costo)
 * - Previsualizar filas
 * - Permitir seleccionar qué filas aplicar
 * - Matching inteligente: barcode > nombre (normalizado)
 * - Permitir override manual por fila: dropdown con filtro (buscable)
 *
 * IMPORTANTE:
 * - No se borra ninguna funcionalidad existente.
 * - Este módulo no toca precios de venta, sólo costos.
 * - Actualiza products.cost_price y (si existe) product_variants.cost_price; si no existe variante, la crea.
 */

export default function GestionCostosImportar({ onNavigate }) {
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  const [importMode, setImportMode] = useState('excel');

  // Catálogo Impulza
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Archivo / parsing
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]); // filas del excel como objeto {col:val}

  // Mapping columnas
  const [matchCol, setMatchCol] = useState('');
  const [costCol, setCostCol] = useState('');

  // Preview + selección
  const [selected, setSelected] = useState({}); // { rowIndex: true }
  const [manualProductByRow, setManualProductByRow] = useState({}); // { rowIndex: product_id }
  const [saving, setSaving] = useState(false);
  const [processingOcr, setProcessingOcr] = useState(false);
  const [confirmPulse, setConfirmPulse] = useState({});
  const [ocrFileData, setOcrFileData] = useState(null);
  const [ocrFileMeta, setOcrFileMeta] = useState(null);
  const [ocrSummary, setOcrSummary] = useState(null);
  const restoredDraftRef = useRef(false);

  const pulse = (key) => {
    setConfirmPulse((p) => ({ ...p, [key]: true }));
    window.setTimeout(() => setConfirmPulse((p) => ({ ...p, [key]: false })), 650);
  };

  const clearImportedData = ({ keepFileMeta = false } = {}) => {
    setFileName('');
    setHeaders([]);
    setRawRows([]);
    setMatchCol('');
    setCostCol('');
    setSelected({});
    setManualProductByRow({});
    setConfirmPulse({});
    setOcrSummary(null);
    if (!keepFileMeta) {
      setOcrFileData(null);
      setOcrFileMeta(null);
    }
  };

  const hydrateImportedRows = ({ importedRows, sourceName, preferredMatchCol = '', preferredCostCol = '', summary = null }) => {
    if (!Array.isArray(importedRows) || importedRows.length === 0) {
      setFileName(sourceName || '');
      setHeaders([]);
      setRawRows([]);
      setMatchCol('');
      setCostCol('');
      setSelected({});
      setManualProductByRow({});
      setOcrSummary(summary);
      return false;
    }

    const allHeaders = Array.from(
      importedRows.reduce((set, row) => {
        Object.keys(row || {}).forEach((key) => set.add(key));
        return set;
      }, new Set())
    );

    const normalizedRows = importedRows.map((row) => {
      const next = {};
      allHeaders.forEach((key) => {
        next[key] = row?.[key] ?? '';
      });
      return next;
    });

    const guessed = guessColumns(allHeaders);
    const safeMatch =
      (preferredMatchCol && allHeaders.includes(preferredMatchCol) && preferredMatchCol) ||
      guessed.matchCol ||
      allHeaders[0] ||
      '';
    const safeCost =
      (preferredCostCol && allHeaders.includes(preferredCostCol) && preferredCostCol) ||
      guessed.costCol ||
      allHeaders.find((h) => h !== safeMatch) ||
      allHeaders[0] ||
      '';

    const initialSel = {};
    normalizedRows.forEach((_, idx) => {
      initialSel[idx] = true;
    });

    setFileName(sourceName || '');
    setHeaders(allHeaders);
    setRawRows(normalizedRows);
    setMatchCol(safeMatch);
    setCostCol(safeCost);
    setSelected(initialSel);
    setManualProductByRow({});
    setOcrSummary(summary);
    return true;
  };

  useEffect(() => {
    if (!orgId) return;
    fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (!orgId || restoredDraftRef.current) return;

    const draft = readImportDraft(orgId);
    restoredDraftRef.current = true;
    if (!draft) return;

    setImportMode(draft.importMode === 'photo' ? 'photo' : 'excel');
    setFileName(typeof draft.fileName === 'string' ? draft.fileName : '');
    setHeaders(Array.isArray(draft.headers) ? draft.headers : []);
    setRawRows(Array.isArray(draft.rawRows) ? draft.rawRows : []);
    setMatchCol(typeof draft.matchCol === 'string' ? draft.matchCol : '');
    setCostCol(typeof draft.costCol === 'string' ? draft.costCol : '');
    setSelected(draft.selected && typeof draft.selected === 'object' ? draft.selected : {});
    setManualProductByRow(
      draft.manualProductByRow && typeof draft.manualProductByRow === 'object'
        ? draft.manualProductByRow
        : {}
    );
    setOcrFileMeta(draft.ocrFileMeta && typeof draft.ocrFileMeta === 'object' ? draft.ocrFileMeta : null);
    setOcrSummary(draft.ocrSummary && typeof draft.ocrSummary === 'object' ? draft.ocrSummary : null);
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !restoredDraftRef.current) return;

    const hasParsedRows = rawRows.length > 0;
    const safeImportMode = importMode === 'photo' ? 'photo' : 'excel';

    if (!hasParsedRows && safeImportMode === 'excel') {
      clearImportDraft(orgId);
      return;
    }

    writeImportDraft(orgId, {
      importMode: safeImportMode,
      fileName: hasParsedRows ? fileName : '',
      headers: hasParsedRows ? headers : [],
      rawRows: hasParsedRows ? rawRows : [],
      matchCol: hasParsedRows ? matchCol : '',
      costCol: hasParsedRows ? costCol : '',
      selected: hasParsedRows ? selected : {},
      manualProductByRow: hasParsedRows ? manualProductByRow : {},
      ocrFileMeta: hasParsedRows && safeImportMode === 'photo' ? ocrFileMeta : null,
      ocrSummary: hasParsedRows ? ocrSummary : null
    });
  }, [
    orgId,
    importMode,
    fileName,
    headers,
    rawRows,
    matchCol,
    costCol,
    selected,
    manualProductByRow,
    ocrFileMeta,
    ocrSummary
  ]);

  const handleImportModeChange = (nextMode) => {
    if (nextMode === importMode) return;
    setImportMode(nextMode);
    clearImportedData();
  };

  const fetchCatalog = async () => {
    setLoading(true);
    try {
      const [resP, resV] = await Promise.all([
        supabase
          .from('products')
          .select('id, name, barcode, cost_price, active')
          .eq('organization_id', orgId)
          .order('name', { ascending: true }),
        supabase
          .from('product_variants')
          .select('id, product_id, barcode, cost_price')
          .eq('organization_id', orgId)
      ]);

      if (resP.error) throw resP.error;
      if (resV.error) throw resV.error;

      setProducts(resP.data || []);
      setVariants(resV.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando catálogo (revisar RLS)');
      setProducts([]);
      setVariants([]);
    } finally {
      setLoading(false);
    }
  };

  const indexes = useMemo(() => {
    const byBarcode = new Map();
    const byName = new Map();

    const safeBarcode = (b) => normalize(String(b || '')).replaceAll(' ', '');

    products.forEach((p) => {
      const bc = safeBarcode(p.barcode);
      const nm = normalize(p.name);
      if (bc) byBarcode.set(bc, p.id);
      if (nm) byName.set(nm, p.id);
    });

    variants.forEach((v) => {
      const bc = safeBarcode(v.barcode);
      if (bc && v.product_id) byBarcode.set(bc, v.product_id);
    });

    return { byBarcode, byName, safeBarcode };
  }, [products, variants]);

  const productOptions = useMemo(() => {
    return (products || [])
      .filter((p) => p.active !== false)
      .map((p) => ({
        id: p.id,
        label: p.name || '(sin nombre)',
        barcode: p.barcode || ''
      }));
  }, [products]);

  // Label dinámico del header “Proveedor”
  const proveedorHeaderLabel = useMemo(() => {
    const m = normalize(matchCol || '');
    const isCodigo =
      m.includes('barcode') ||
      m.includes('ean') ||
      m.includes('gtin') ||
      m.includes('codigo') ||
      m.includes('sku') ||
      m.includes('codbarras') ||
      m.includes('codigo barra');
    return isCodigo ? 'Código del proveedor' : 'Nombre del proveedor';
  }, [matchCol]);

  const onFile = async (file) => {
    if (!file) return;

    let XLSX;
    try {
      const mod = await import('xlsx');
      XLSX = mod?.default || mod;
    } catch (e) {
      console.error(e);
      toast.error('Falta dependencia: instalá "xlsx" (npm i xlsx)');
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const wb = XLSX.read(arrayBuffer, { type: 'array' });

      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) {
        toast.error('El Excel no tiene hojas');
        return;
      }

      const ws = wb.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });

      if (!Array.isArray(json) || json.length === 0) {
        toast.error('El Excel está vacío');
        clearImportedData();
        return;
      }

      hydrateImportedRows({
        importedRows: json,
        sourceName: file.name || 'archivo.xlsx'
      });

      toast.success('Excel cargado. Revisá el mapeo y aplicá.');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo leer el Excel');
    }
  };

  const handleOcrFileChange = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!isAllowedOcrFile(file)) {
      toast.error('Solo se permiten imagenes o PDF para OCR.');
      return;
    }

    if (file.size > MAX_OCR_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`El archivo es muy pesado. Intenta con uno menor a ${MAX_OCR_FILE_SIZE_MB}MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setOcrFileData(reader.result);
      setOcrFileMeta({
        name: file.name,
        type: file.type || (isPdfFile(file) ? 'application/pdf' : 'image/*'),
        size: file.size,
        isPdf: isPdfFile(file)
      });
      clearImportedData({ keepFileMeta: true });
      setFileName(file.name || 'foto-lista-precios');
      toast.success('Archivo listo para procesar.');
    };
    reader.readAsDataURL(file);
  };

  const processOcrRows = (data) => {
    const items = Array.isArray(data?.items_detectados) ? data.items_detectados : [];
    const rows = items
      .map((item) => {
        const codigo = String(
          item?.codigo_detectado ??
            item?.codigo ??
            item?.sku_detectado ??
            ''
        ).trim();
        const producto = String(
          item?.nombre_detectado ??
            item?.producto_detectado ??
            item?.descripcion_detectada ??
            item?.descripcion ??
            ''
        ).trim();
        const costo = toNumber(item?.costo_detectado ?? item?.precio_detectado ?? item?.costo ?? item?.precio ?? 0);
        const textoOriginal = String(item?.texto_original ?? '').trim();

        if (!codigo && !producto && !textoOriginal && !costo) return null;

        return {
          'Codigo detectado': codigo,
          'Producto detectado': producto,
          'Costo detectado': costo,
          'Texto original': textoOriginal
        };
      })
      .filter(Boolean);

    const preferredMatchCol = rows.some((row) => String(row?.['Codigo detectado'] || '').trim())
      ? 'Codigo detectado'
      : 'Producto detectado';

    return {
      rows,
      preferredMatchCol,
      preferredCostCol: 'Costo detectado',
      summary: {
        proveedor: data?.proveedor_nombre || null,
        fecha: data?.fecha_lista || data?.fecha || null,
        moneda: data?.moneda || null,
        columnaPrecio: data?.columna_precio_elegida || null,
        itemsCount: rows.length
      }
    };
  };

  const handleProcessOcr = async () => {
    if (!ocrFileData || !ocrFileMeta) {
      toast.error('Primero subi una foto para procesar.');
      return;
    }

    setProcessingOcr(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (!accessToken) {
        throw new Error('No hay sesiÃ³n activa. Recarga la pÃ¡gina.');
      }

      const { data, error } = await supabase.functions.invoke('ocr-lista-precios', {
        body: {
          image: ocrFileData,
          fileName: ocrFileMeta.name,
          fileType: ocrFileMeta.type
        },
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (error) {
        throw new Error(error.message || 'Error al conectar con el OCR.');
      }

      if (data?.ok === false) {
        throw new Error(data.error || 'La IA no pudo leer la lista de precios.');
      }

      const normalized = processOcrRows(data?.data || {});
      if (!normalized.rows.length) {
        throw new Error('No se detectaron filas de productos legibles en la foto.');
      }

      hydrateImportedRows({
        importedRows: normalized.rows,
        sourceName: ocrFileMeta.name || 'foto-lista-precios',
        preferredMatchCol: normalized.preferredMatchCol,
        preferredCostCol: normalized.preferredCostCol,
        summary: normalized.summary
      });

      try {
        if (profile?.organization_id) {
          await supabase.from('usage_logs').insert({
            organization_id: profile.organization_id,
            user_id: profile.user_id,
            module_id: 'OCR_COSTOS',
            button_clicked: 'scan_price_list',
            time_spent_seconds: 5
          });
        }
      } catch (logError) {
        console.warn('No se pudo registrar log de OCR de costos', logError);
      }

      toast.success('Foto procesada. Revisa las coincidencias y tilda lo que quieras actualizar.');
    } catch (error) {
      console.error(error);
      toast.error(`Falla OCR: ${error.message || 'Error inesperado'}`);
    } finally {
      setProcessingOcr(false);
    }
  };

  const suggestedMatches = useMemo(() => {
    if (!rawRows.length || !matchCol) return [];

    return rawRows.map((row, idx) => {
      const rawKey = row?.[matchCol];
      const rawNameFallback =
        row?.['Producto detectado'] ||
        row?.['Nombre detectado'] ||
        row?.['Nombre'] ||
        row?.['nombre'] ||
        row?.['Producto'] ||
        row?.['producto'] ||
        row?.['Texto original'] ||
        '';

      const key = String(rawKey ?? rawNameFallback ?? '').trim();
      const keyNormBarcode = indexes.safeBarcode(key);

      let productId = null;
      let method = 'none';
      let confidence = 0;

      if (keyNormBarcode && indexes.byBarcode.has(keyNormBarcode)) {
        productId = indexes.byBarcode.get(keyNormBarcode);
        method = 'barcode';
        confidence = 95;
      } else {
        const kn = normalize(key);
        if (kn && indexes.byName.has(kn)) {
          productId = indexes.byName.get(kn);
          method = 'name_exact';
          confidence = 85;
        } else if (kn) {
          const found = productOptions.find((o) => normalize(o.label).includes(kn) || kn.includes(normalize(o.label)));
          if (found) {
            productId = found.id;
            method = 'name_fuzzy';
            confidence = 65;
          }
        }
      }

      return { idx, suggestedProductId: productId, method, confidence };
    });
  }, [rawRows, matchCol, indexes, productOptions]);

  const previewRows = useMemo(() => {
    if (!rawRows.length) return [];

    return rawRows.map((row, idx) => {
      const s = suggestedMatches[idx] || { suggestedProductId: null, method: 'none', confidence: 0 };
      const manual = manualProductByRow[idx] || null;
      const finalProductId = manual || s.suggestedProductId;

      const cost = costCol ? toNumber(row?.[costCol]) : 0;

      const prod = products.find((p) => p.id === finalProductId) || null;
      const currentCost = Number(prod?.cost_price || 0);

      const diff = cost - currentCost;

      return {
        idx,
        sourceKey: String(row?.[matchCol] ?? '').trim(),
        sourceCost: cost,
        suggestedProductId: s.suggestedProductId,
        finalProductId,
        method: s.method,
        confidence: s.confidence,
        currentCost,
        diff,
        raw: row
      };
    });
  }, [rawRows, suggestedMatches, manualProductByRow, products, matchCol, costCol]);

  const totals = useMemo(() => {
    const base = previewRows.filter((r) => selected[r.idx]);
    const ok = base.filter((r) => !!r.finalProductId && r.sourceCost > 0).length;
    const missing = base.filter((r) => !r.finalProductId).length;
    const cost0 = base.filter((r) => r.sourceCost <= 0).length;
    return { selectedCount: base.length, ok, missing, cost0 };
  }, [previewRows, selected]);

  const toggleAll = () => {
    if (!previewRows.length) return;
    const allSelected = previewRows.every((r) => selected[r.idx]);
    const next = {};
    previewRows.forEach((r) => {
      next[r.idx] = !allSelected;
    });
    setSelected(next);
  };

  const applyImport = async () => {
    if (!orgId) return;
    const base = previewRows.filter((r) => selected[r.idx]);

    if (base.length === 0) {
      toast.error('No hay filas seleccionadas');
      return;
    }

    const invalid = base.filter((r) => !r.finalProductId || r.sourceCost <= 0);
    if (invalid.length > 0) {
      toast.error('Hay filas seleccionadas sin producto asignado o precio inválido (0).');
      return;
    }

    setSaving(true);
    try {
      const variantByProduct = new Map();
      variants.forEach((v) => {
        if (!v.product_id) return;
        if (!variantByProduct.has(v.product_id)) variantByProduct.set(v.product_id, v);
      });

      for (const r of base) {
        const productId = r.finalProductId;
        const newCost = Number(r.sourceCost || 0);

        const pRes = await supabase.from('products').update({ cost_price: newCost }).eq('id', productId);
        if (pRes.error) throw pRes.error;

        const existing = variantByProduct.get(productId) || null;
        if (existing?.id) {
          const vRes = await supabase.from('product_variants').update({ cost_price: newCost }).eq('id', existing.id);
          if (vRes.error) throw vRes.error;
        } else {
          const ins = await supabase.from('product_variants').insert({
            organization_id: orgId,
            product_id: productId,
            cost_price: newCost,
            price: 0,
            sku: null,
            barcode: null,
            stock_quantity: 0
          });
          if (ins.error) throw ins.error;
        }
      }

      toast.success('Costos actualizados correctamente');
      pulse('applyImport');
      await fetchCatalog();
    } catch (e) {
      console.error(e);
      toast.error('Fallo al aplicar importación (revisar RLS/policies)');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-[#F2F9FF] p-8">
        <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
          <FileSpreadsheet className="text-slate-300 animate-pulse" size={18} />
        </div>
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando Importador…</p>
      </div>
    );
  }

  // ✅ Estilos de “zona Excel” (más oscuro + separador visual)
  const EXCEL_BG_TH = 'bg-slate-100 text-slate-600';
  const EXCEL_BG_TD = 'bg-slate-100';
  const EXCEL_DIVIDER = 'border-r border-slate-200';

  return (
    <div className="w-full h-full p-5 md:p-6 animate-nova-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md">
            <UploadCloud size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">
              Importar costos
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Subi un excel o una foto de la lista del proveedor para actualizar costos masivamente
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => onNavigate?.('gestion-costos')}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            title="Volver"
          >
            <ArrowLeft size={14} /> Volver
          </button>

          <button
            onClick={fetchCatalog}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            title="Actualizar catálogo"
          >
            <RefreshCcw size={14} /> Actualizar
          </button>

          <button
            onClick={applyImport}
            disabled={saving}
            className={`relative px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60
              ${confirmPulse.applyImport ? 'scale-[1.02]' : ''}`}
            title="Aplicar actualización de costos (solo filas seleccionadas)"
          >
            {saving ? <RefreshCcw size={14} className="animate-spin" /> : <DollarSign size={14} />}
            Aplicar costos

            {confirmPulse.applyImport ? (
              <span className="absolute -right-2 -top-2 w-7 h-7 rounded-full bg-[#0B2A36] flex items-center justify-center shadow-lg">
                <CheckCircle2 size={16} className="text-[#3FF2AA]" />
              </span>
            ) : null}
          </button>
        </div>
      </div>

      {/* Upload */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => handleImportModeChange('excel')}
            className={`px-4 py-2.5 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all ${
              importMode === 'excel'
                ? 'bg-[#0B2A36] text-white border-[#0B2A36] shadow-md'
                : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
            }`}
          >
            Importar por Excel
          </button>

          <button
            type="button"
            onClick={() => handleImportModeChange('photo')}
            className={`px-4 py-2.5 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all inline-flex items-center gap-2 ${
              importMode === 'photo'
                ? 'bg-[#0B2A36] text-white border-[#0B2A36] shadow-md'
                : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <Camera size={14} />
            Cargar mediante una foto
          </button>
        </div>

        {importMode === 'excel' ? (
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Archivo</div>
            <div className="text-[12px] font-black text-slate-800 truncate">
              {fileName ? fileName : 'Ningún archivo seleccionado'}
            </div>
            <div className="text-[10px] font-bold text-slate-400">
              Recomendado: hoja 1 con columna de “Código/Nombre” + “Costo/Precio”.
            </div>
          </div>

          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-md hover:brightness-110 transition-all text-[10px] font-black uppercase tracking-widest">
            <FileSpreadsheet size={14} />
            Seleccionar Excel
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </label>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Foto de lista</div>
                <div className="text-[12px] font-black text-slate-800 truncate">
                  {ocrFileMeta?.name || 'Ninguna foto seleccionada'}
                </div>
                <div className="text-[10px] font-bold text-slate-400">
                  Saca una foto clara de la lista y despues tilda los productos que quieras actualizar.
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-md hover:brightness-110 transition-all text-[10px] font-black uppercase tracking-widest">
                  <Camera size={14} />
                  Seleccionar foto
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    capture="environment"
                    className="hidden"
                    onChange={handleOcrFileChange}
                  />
                </label>

                <button
                  type="button"
                  onClick={handleProcessOcr}
                  disabled={!ocrFileData || processingOcr}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-md hover:shadow-lg transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-60"
                >
                  {processingOcr ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />}
                  Procesar foto
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Estado</div>
                <div className="mt-1 text-[12px] font-black text-slate-800">
                  {processingOcr ? 'Procesando OCR...' : ocrFileMeta ? 'Archivo listo para leer' : 'Esperando foto'}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Formato</div>
                <div className="mt-1 text-[12px] font-black text-slate-800">
                  {ocrFileMeta ? (ocrFileMeta.isPdf ? 'PDF' : 'Imagen') : 'Imagen o PDF'}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Peso</div>
                <div className="mt-1 text-[12px] font-black text-slate-800">
                  {ocrFileMeta ? formatFileSize(ocrFileMeta.size) : `Hasta ${MAX_OCR_FILE_SIZE_MB} MB`}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {headers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Filas detectadas</div>
            <div className="mt-1 text-2xl font-black text-slate-800">{previewRows.length}</div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Seleccionadas</div>
            <div className="mt-1 text-2xl font-black text-slate-800">{totals.selectedCount}</div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Listas para aplicar</div>
            <div className={`mt-1 text-2xl font-black ${totals.ok > 0 ? 'text-emerald-700' : 'text-slate-800'}`}>{totals.ok}</div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              {ocrSummary ? 'Proveedor detectado' : 'Pendientes de revisar'}
            </div>
            <div className={`mt-1 text-[13px] font-black ${totals.missing + totals.cost0 > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
              {ocrSummary?.proveedor || `${totals.missing + totals.cost0} fila(s)`}
            </div>
            {ocrSummary ? (
              <div className="mt-1 text-[10px] font-bold text-slate-400">
                {ocrSummary.itemsCount} item(s)
                {ocrSummary.fecha ? ` - ${ocrSummary.fecha}` : ''}
                {ocrSummary.columnaPrecio ? ` - ${ocrSummary.columnaPrecio}` : ''}
              </div>
            ) : (
              <div className="mt-1 text-[10px] font-bold text-slate-400">
                Sin producto asignado o sin precio valido
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mapping */}
      {headers.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="relative">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Columna de Match</div>
              <div className="text-[10px] font-bold text-slate-400">
                Se usa para identificar el producto (código o nombre)
              </div>
              <div className="mt-2">
                <select
                  value={matchCol}
                  onChange={(e) => setMatchCol(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-[11px] font-bold outline-none focus:border-[#51A7FD]"
                >
                  {headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="relative">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Columna de Precio</div>
              <div className="text-[10px] font-bold text-slate-400">Se debe seleccionar la columna que tiene el precio del proveedor</div>
              <div className="mt-2">
                <select
                  value={costCol}
                  onChange={(e) => setCostCol(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-[11px] font-bold outline-none focus:border-[#51A7FD]"
                >
                  {headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-bold text-slate-500">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-xl bg-slate-50 border border-slate-200">
              <Link2 size={14} className="text-slate-300" /> Sugerencias automáticas activas
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-xl bg-slate-50 border border-slate-200">
              <AlertTriangle size={14} className="text-slate-300" /> Si el match falla, asigná manualmente
            </span>
          </div>
        </div>
      )}

      {/* Grid Preview */}
      {headers.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <th className="px-4 py-4 w-10 text-center">
                    <button
                      onClick={toggleAll}
                      title="Seleccionar todo"
                      className="bg-transparent hover:bg-transparent active:bg-transparent rounded-lg p-1"
                    >
                      {previewRows.length > 0 && previewRows.every((r) => selected[r.idx]) ? (
                        <CheckSquare size={16} className="text-[#51A7FD]" />
                      ) : (
                        <Square size={16} className="text-slate-300" />
                      )}
                    </button>
                  </th>

                  {/* 👇 Zona Excel con badge + separador */}
                  <th className={`px-4 py-4 ${EXCEL_BG_TH} ${EXCEL_DIVIDER}`}>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-lg bg-slate-200 text-slate-700 text-[9px] font-black tracking-widest">
                        EXCEL
                      </span>
                      <span>{proveedorHeaderLabel}</span>
                    </div>
                  </th>

                  <th className={`px-4 py-4 ${EXCEL_BG_TH} ${EXCEL_DIVIDER}`}>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-lg bg-slate-200 text-slate-700 text-[9px] font-black tracking-widest">
                        EXCEL
                      </span>
                      <span>Precio del proveedor</span>
                    </div>
                  </th>

                  <th className="px-4 py-4">Producto {BRAND_NAME} (editable)</th>
                  <th className="px-4 py-4 text-right">Costo actual en {BRAND_NAME}</th>
                  <th className="px-4 py-4 text-right">Diferencia</th>
                  <th className="px-4 py-4">Match</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-50">
                {previewRows.slice(0, 300).map((r) => {
                  const isChecked = !!selected[r.idx];
                  const ok = !!r.finalProductId && r.sourceCost > 0;

                  const diffTone =
                    r.diff > 0 ? 'text-rose-700' : r.diff < 0 ? 'text-emerald-700' : 'text-slate-500';

                  return (
                    <tr key={r.idx} className={`hover:bg-slate-50/60 transition-all ${isChecked ? 'bg-blue-50/20' : ''}`}>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => setSelected((p) => ({ ...p, [r.idx]: !p[r.idx] }))}
                          title="Incluir/excluir fila"
                          className="bg-transparent hover:bg-transparent active:bg-transparent rounded-lg p-1"
                        >
                          {isChecked ? (
                            <CheckSquare size={16} className="text-[#51A7FD]" />
                          ) : (
                            <Square size={16} className="text-slate-300" />
                          )}
                        </button>
                      </td>

                      {/* 👇 Zona Excel con separador vertical */}
                      <td className={`px-4 py-4 ${EXCEL_BG_TD} ${EXCEL_DIVIDER}`}>
                        <div className="text-[11px] font-black text-slate-800 truncate">
                          {String(r.raw?.[matchCol] ?? '').trim() || '(vacío)'}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 truncate">Col: {matchCol}</div>
                      </td>

                      <td className={`px-4 py-4 ${EXCEL_BG_TD} ${EXCEL_DIVIDER}`}>
                        <div className={`text-[11px] font-black ${r.sourceCost > 0 ? 'text-slate-900' : 'text-amber-700'}`}>
                          $ {money(r.sourceCost)}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 truncate">Col: {costCol}</div>
                      </td>

                      <td className="px-4 py-4 min-w-[360px]">
                        <ProductCombo
                          value={r.finalProductId || ''}
                          options={productOptions}
                          onChange={(pid) => setManualProductByRow((p) => ({ ...p, [r.idx]: pid }))}
                          placeholder={r.suggestedProductId ? 'Sugerido' : 'Sin sugerencia'}
                        />
                      </td>

                      <td className="px-4 py-4 text-right">
                        <div className="text-[11px] font-black text-slate-700">$ {money(r.currentCost)}</div>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <div className={`text-[11px] font-black ${diffTone}`}>
                          {r.diff >= 0 ? '+' : ''}
                          {money(r.diff)}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div
                          className={`inline-flex items-center gap-2 px-2 py-1 rounded-xl border text-[10px] font-black uppercase tracking-widest
                          ${ok ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}
                        >
                          {ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                          {r.method === 'barcode'
                            ? `Barras ${r.confidence}%`
                            : r.method === 'name_exact'
                            ? `Nombre ${r.confidence}%`
                            : r.method === 'name_fuzzy'
                            ? `Fuzzy ${r.confidence}%`
                            : 'Sin match'}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {previewRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-20 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.25em]">
                      Cargá un Excel para ver la previsualización
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {previewRows.length > 0 && (
            <div className="p-3 border-t border-slate-100 bg-slate-50/40 flex flex-col md:flex-row md:items-center justify-between gap-2">
              <div className="text-[10px] font-bold text-slate-500">
                Mostrando hasta 300 filas (performance). Si necesitás 2000+, lo pasamos a paginado/batch.
              </div>

              <button
                type="button"
                onClick={() => {
                  setManualProductByRow({});
                  toast.success('Sugerencias reiniciadas');
                }}
                className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
              >
                <X size={14} /> Reiniciar sugerencias
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
